import { Link, useParams, Navigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { blogArticles, type BlogArticle } from "@/mocks/blogContent";
import JsonLd from "@/components/base/JsonLd";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/pages/home/components/Footer";

const siteUrl = import.meta.env.VITE_SITE_URL || "https://mulebuyspreadsheets.autos";

function buildArticleJsonLd(article: BlogArticle) {
  const articleUrl = `${siteUrl}/blog/${article.slug}`;
  return {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: article.title,
    description: article.metaDescription,
    image: article.imageUrl,
    datePublished: article.publishedDate,
    dateModified: article.publishedDate,
    author: {
      "@type": "Organization",
      name: article.author,
      url: siteUrl,
    },
    publisher: {
      "@type": "Organization",
      name: "Mulebuy Spreadsheet",
      url: siteUrl,
      logo: {
        "@type": "ImageObject",
        url: `${siteUrl}/favicon.ico`,
      },
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": articleUrl,
    },
    inLanguage: "en",
    isAccessibleForFree: true,
  };
}

function buildBreadcrumbJsonLd(article: BlogArticle) {
  const articleUrl = `${siteUrl}/blog/${article.slug}`;
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: siteUrl,
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Blog",
        item: `${siteUrl}/`,
      },
      {
        "@type": "ListItem",
        position: 3,
        name: article.title,
        item: articleUrl,
      },
    ],
  };
}

function buildFaqPageJsonLd(article: BlogArticle) {
  const faqSections = article.sections.filter((s) => s.type === "faq" && s.items);
  if (faqSections.length === 0) return null;
  const allQuestions = faqSections.flatMap((s) => s.items || []);
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: allQuestions.map((item) => ({
      "@type": "Question",
      name: item.q,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.a,
      },
    })),
  };
}

function TableBlock({ title, headers, rows }: { title: string; headers: string[]; rows: string[][] }) {
  return (
    <div className="my-6 md:my-8 overflow-x-auto rounded-lg border border-background-200">
      <table className="w-full text-left">
        <thead>
          <tr className="bg-secondary-100">
            {headers.map((h, i) => (
              <th key={i} className="px-3 md:px-5 py-2.5 md:py-3 text-xs md:text-sm font-semibold text-foreground-900 whitespace-nowrap">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, ri) => (
            <tr key={ri} className={ri % 2 === 0 ? "bg-background-50" : "bg-background-100/50"}>
              {row.map((cell, ci) => (
                <td key={ci} className={`px-3 md:px-5 py-2.5 text-xs md:text-sm ${ci === 0 ? "font-medium text-foreground-800" : "text-foreground-600"}`}>
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {title && (
        <p className="px-3 md:px-5 py-2 text-xs text-foreground-500 bg-background-50 border-t border-background-200">
          {title}
        </p>
      )}
    </div>
  );
}

function FAQBlock({ items }: { items: { q: string; a: string }[] }) {
  const [openIdx, setOpenIdx] = useState<number | null>(null);

  return (
    <div className="my-6 md:my-8 space-y-3" itemScope itemType="https://schema.org/FAQPage">
      {items.map((item, idx) => (
        <div key={idx} className="bg-background-100 rounded-lg border border-background-200 overflow-hidden" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
          <button
            onClick={() => setOpenIdx(openIdx === idx ? null : idx)}
            className="w-full flex items-center justify-between px-4 py-3 text-left cursor-pointer hover:bg-background-50 transition-colors"
          >
            <span className="text-sm font-medium text-foreground-900 pr-4" itemProp="name">{item.q}</span>
            <i className={`ri-arrow-down-s-line text-foreground-500 transition-transform duration-200 shrink-0 ${openIdx === idx ? "rotate-180" : ""}`}></i>
          </button>
          <div className={`transition-all duration-300 overflow-hidden ${openIdx === idx ? "max-h-96" : "max-h-0"}`} itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
            <p className="px-4 pb-4 text-xs md:text-sm text-foreground-600 leading-relaxed" itemProp="text">{item.a}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

function ArticleContent({ article }: { article: BlogArticle }) {
  const faqJsonLd = buildFaqPageJsonLd(article);

  return (
    <article className="max-w-3xl mx-auto px-4 md:px-8 lg:px-12 py-10 md:py-16">

      {faqJsonLd && <JsonLd data={faqJsonLd} />}

      <div className="w-full h-56 sm:h-72 md:h-96 rounded-lg overflow-hidden mb-8 md:mb-12">
        <img
          src={article.imageUrl}
          alt={article.title}
          className="w-full h-full object-cover object-top"
        />
      </div>

      <header className="mb-10 md:mb-14">
        <div className="flex items-center gap-2 text-xs text-foreground-500 mb-3">
          <Link to="/" className="hover:text-primary-600 transition-colors">
            Home
          </Link>
          <span>/</span>
          <span>Blog</span>
        </div>
        <h1 className="font-heading text-2xl md:text-4xl lg:text-5xl font-bold text-foreground-900 leading-tight mb-4">
          {article.title}
        </h1>
        <div className="flex flex-wrap items-center gap-3 text-xs md:text-sm text-foreground-500">
          <span>{article.author}</span>
          <span>·</span>
          <time dateTime={article.publishedDate}>{article.publishedDate}</time>
          <span>·</span>
          <span>10 min read</span>
        </div>
      </header>

      <div className="space-y-5">
        {article.sections.map((section, idx) => {
          switch (section.type) {
            case "h2":
              return (
                <h2 key={idx} className="font-heading text-xl md:text-2xl font-semibold text-foreground-900 mt-10 md:mt-12 mb-4">
                  {section.content}
                </h2>
              );
            case "h3":
              return (
                <h3 key={idx} className="font-heading text-lg md:text-xl font-semibold text-foreground-800 mt-6 md:mt-8 mb-3">
                  {section.content}
                </h3>
              );
            case "paragraph":
              return (
                <p key={idx} className="text-sm md:text-base text-foreground-700 leading-relaxed">
                  {section.content}
                </p>
              );
            case "table":
              return (
                <TableBlock
                  key={idx}
                  title={section.title || ""}
                  headers={section.headers || []}
                  rows={section.rows || []}
                />
              );
            case "faq":
              return (
                <FAQBlock key={idx} items={section.items || []} />
              );
            case "cta":
              return (
                <div key={idx} className="my-8 md:my-10 bg-accent-50 rounded-lg p-5 md:p-8 text-center">
                  <p className="text-sm md:text-base text-accent-900 leading-relaxed mb-4">
                    {section.content}
                  </p>
                  <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                    <a
                      href="https://kaspreadsheet.com/"
                      target="_blank"
                      rel="noopener nofollow"
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 hover:bg-primary-600 text-sm font-semibold transition-colors whitespace-nowrap"
                    >
                      Access Mulebuy Spreadsheet
                      <i className="ri-arrow-right-up-line"></i>
                    </a>
                    <Link
                      to="/"
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-background-50 text-foreground-900 hover:bg-background-100 text-sm font-medium transition-colors whitespace-nowrap"
                    >
                      Back to Homepage
                      <i className="ri-home-line"></i>
                    </Link>
                  </div>
                </div>
              );
            default:
              return null;
          }
        })}
      </div>

      <div className="mt-12 md:mt-16 pt-8 border-t border-background-200">
        <div className="bg-background-100 rounded-lg p-5 md:p-6 text-center">
          <h3 className="font-heading text-lg md:text-xl font-semibold text-foreground-900 mb-2">
            Ready to Start Sourcing?
          </h3>
          <p className="text-sm text-foreground-600 mb-4">
            Access the most updated Mulebuy Spreadsheet and begin shopping smarter today.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 hover:bg-primary-600 text-sm font-semibold transition-colors whitespace-nowrap"
            >
              Get the Spreadsheet
              <i className="ri-arrow-right-up-line"></i>
            </a>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-background-50 text-foreground-900 hover:bg-background-100 text-sm font-medium transition-colors whitespace-nowrap"
            >
              <i className="ri-home-line"></i>
              Return to Homepage
            </Link>
          </div>
        </div>
      </div>
    </article>
  );
}

export default function BlogArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const article = blogArticles.find((a) => a.slug === slug);

  if (!article) {
    return <Navigate to="/" replace />;
  }

  return (
    <>
      <JsonLd data={buildArticleJsonLd(article)} />
      <JsonLd data={buildBreadcrumbJsonLd(article)} />
      <Navbar />
      <main className="pt-20 md:pt-24">
        <ArticleContent article={article} />
      </main>
      <Footer />
    </>
  );
}